import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Select, Tooltip, Typography } from "antd";
import { SyncOutlined, WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import FormItem from "../../../components/FormItem";
import filterOptions from "../../../utils/filterOptions";
import ProjectStatus from "../../../enums/ProjectStatus";
import ProjectTypes from "../../../enums/ProjectTypes";
import { IDetailedProject } from "../../Project";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { LinkProject as Description, ProjectLabel } from "./styled";
import { Filters, FiltersCard } from "../../../components/Filters";
import {
  Card,
  CardBoard,
  CardScroll,
  Container,
  Content,
  ContentAction,
  Identifier,
  Page,
  PageTitle,
  Title,
} from "../styled";
import { removeNullKeys } from "../../../utils/formatters";
import { IProjectType } from "../../Projects";
import { Label } from "../../../components/ui";
import { useAuth } from "../../../hooks/auth";
import IUserRoles from "../../../interfaces/IUserRoles";
import UserRoles from "../../../enums/UserRoles";
import isWarningActivity from "../../../utils/isWarningActivity";
import IStatusImpediment from "../../../interfaces/IStatusImpediment";

const { Text } = Typography;

export interface IProjectStatus {
  label: string;
  status: ProjectStatus;
  data: IDetailedProject[];
  text: string;
  isDev: boolean;
}

const KanbanProject = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<IDetailedProject[]>([]);
  const [projectsTypes, setProjectsTypes] = useState<IProjectType[]>([]);
  const [statusProject, setStatusProject] = useState<IProjectStatus[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterTypeProjects, setFilterTypeProjects] = useState<
    string | undefined
  >(undefined);

  const { addToast } = useToast();

  const { t } = useTranslation(["projects"]);

  const projectOption = useMemo(() => {
    return projectsTypes.map(projectType => ({
      value: projectType.id,
      label: t(`project-types.options.${projectType.acronym}.label`),
    }));
  }, [t, projectsTypes]);

  const { user } = useAuth();

  const isManagerOrAdmin: boolean = useMemo(() => {
    return (
      user.role === IUserRoles.Manager ||
      user.role === IUserRoles.Administrative
    );
  }, [user.role]);

  useEffect(() => {
    api
      .get<IProjectType[]>("/project-types")
      .then(response => setProjectsTypes(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar tipos de projetos",
          description: "Ocorreu um erro ao buscar os tipos de projetos",
        });
      });
  }, [addToast]);

  const generateStatusProject = useCallback(
    (projectsResponse: IDetailedProject[]) => {
      const status_project_admin: IProjectStatus[] = [
        {
          label: t(`status.options.${ProjectStatus.Understanding}.label`),
          status: ProjectStatus.Understanding,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Estimate}.label`),
          status: ProjectStatus.Estimate,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.ProposalSending}.label`),
          status: ProjectStatus.ProposalSending,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.WaitingApproval}.label`),
          status: ProjectStatus.WaitingApproval,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Approved}.label`),
          status: ProjectStatus.Approved,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Assigning}.label`),
          status: ProjectStatus.Assigning,
          data: [],
          text: "Sem kanban",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.WaitingDevelopment}.label`),
          status: ProjectStatus.WaitingDevelopment,
          data: [],
          text: "Acessar kanban",
          isDev: true,
        },
        {
          label: t(`status.options.${ProjectStatus.Development}.label`),
          status: ProjectStatus.Development,
          data: [],
          text: "Acessar kanban",
          isDev: true,
        },
        {
          label: t(`status.options.${ProjectStatus.Concluded}.label`),
          status: ProjectStatus.Concluded,
          data: [],
          text: "Kanban finalizado",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Disapproved}.label`),
          status: ProjectStatus.Disapproved,
          data: [],
          text: "Kanban finalizado",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Suspended}.label`),
          status: ProjectStatus.Suspended,
          data: [],
          text: "Kanban finalizado",
          isDev: false,
        },
        {
          label: t(`status.options.${ProjectStatus.Canceled}.label`),
          status: ProjectStatus.Canceled,
          data: [],
          text: "Kanban finalizado",
          isDev: false,
        },
      ];
      const status_project_dev: IProjectStatus[] = [
        {
          label: t(`status.options.${ProjectStatus.WaitingDevelopment}.label`),
          status: ProjectStatus.WaitingDevelopment,
          data: [],
          text: "Acessar kanban",
          isDev: true,
        },
        {
          label: t(`status.options.${ProjectStatus.Development}.label`),
          status: ProjectStatus.Development,
          data: [],
          text: "Acessar kanban",
          isDev: true,
        },
        {
          label: t(`status.options.${ProjectStatus.Concluded}.label`),
          status: ProjectStatus.Concluded,
          data: [],
          text: "Kanban finalizado",
          isDev: false,
        },
      ];

      const status_project = isManagerOrAdmin
        ? status_project_admin
        : status_project_dev;

      projectsResponse.forEach(project => {
        const projectStatus = (project.status as unknown) as ProjectStatus;
        const index = status_project.findIndex(
          pjt => pjt.status === projectStatus,
        );
        if (index !== -1) status_project[index].data.push(project);
      });

      setStatusProject(status_project);
    },
    [t],
  );

  const Params = useMemo(() => {
    return {
      project_type_id: filterTypeProjects,
    };
  }, [filterTypeProjects]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IDetailedProject[]>("/projects", {
        params: removeNullKeys(Params),
      })
      .then(response => {
        setProjects(response.data);
        generateStatusProject(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar projetos",
          description: "Ocorreu um erro ao buscar os projetos",
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, generateStatusProject, Params]);

  const handleFilter = useCallback((): void => {
    if (showFilters) {
      setFilterTypeProjects(undefined);
    }
    setShowFilters(old => !old);
  }, [showFilters]);

  const generateText = (
    isDev: boolean,
    text: string,
    projectType: ProjectTypes,
    project_id: string,
  ): JSX.Element => {
    if (!isDev) return <>{text}</>;

    if (projectType !== ProjectTypes.Allocation)
      return <Link to={`/app/kanban/projects/${project_id}`}>{text}</Link>;
    return <>Kanban não disponível</>;
  };

  if (loading && !projects.length && !Params?.project_type_id) {
    return <SyncOutlined spin style={{ color: "orange" }} />;
  }

  if (!loading && !projects.length && !Params?.project_type_id) {
    return <PageTitle>Nenhum Projeto Encontrado</PageTitle>;
  }

  return (
    <>
      <ContentAction>
        <Button type="primary">
          <Link to="/app/kanban/administrative">Administrativo</Link>
        </Button>
        <Button type="primary">
          <Link to="/app/kanban/development">Desenvolvimento</Link>
        </Button>
      </ContentAction>
      <Page>
        <PageTitle>
          Kanban projetos{" "}
          <Filters showFilters={showFilters} setShowFilters={handleFilter} />
        </PageTitle>
        {showFilters ? (
          <FiltersCard>
            <Col lg={12} md={12} sm={24} xl={9} xs={24}>
              <div>
                <Label color="#575962">Tipos de Projetos</Label>
              </div>
              <FormItem>
                <Select
                  placeholder="Selecione um tipo de projeto"
                  showSearch
                  allowClear
                  disabled={loading}
                  onChange={projectType => setFilterTypeProjects(projectType)}
                  options={projectOption}
                  filterOption={filterOptions}
                />
              </FormItem>
            </Col>
          </FiltersCard>
        ) : (
          <></>
        )}
        <Container>
          <CardScroll>
            {statusProject?.map(pjts => (
              <CardBoard key={pjts.status}>
                <Title>{pjts.label}</Title>
                <Content>
                  {pjts.data?.map(pjt => (
                    <Card
                      key={pjt.id}
                      typeProjectColor={pjt.projectType.acronym}
                    >
                      <div style={{ display: "flex" }}>
                        <Identifier>
                          {pjt.identifier || "Sem Identificador"}
                        </Identifier>
                        {isWarningActivity(pjt?.requirements) ? (
                          <Tooltip title="Projeto possue atividade(s) com impedimento">
                            <Button
                              type="link"
                              style={{ marginTop: "-5px" }}
                              icon={<WarningOutlined />}
                            />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </div>
                      <Description to={`/app/projects/${pjt.id}`}>
                        {pjt.short_description}
                      </Description>
                      <ProjectLabel typeProjectColor={pjt.projectType.acronym}>
                        {t(
                          `project-types.options.${pjt.projectType.acronym}.label`,
                        )}
                      </ProjectLabel>
                      <Text
                        type="secondary"
                        style={{
                          whiteSpace: "nowrap",
                          pointerEvents:
                            user.role === UserRoles.Developer &&
                            pjt.requirements?.some(
                              req => req.consultant_id !== user.id,
                            )
                              ? "none"
                              : "auto",
                          opacity:
                            user.role === UserRoles.Developer &&
                            pjt.requirements?.some(
                              req => req.consultant_id !== user.id,
                            )
                              ? "0.5"
                              : "1",
                        }}
                      >
                        {generateText(
                          pjts.isDev,
                          pjts.text,
                          pjt.projectType.acronym,
                          pjt.id,
                        )}
                      </Text>
                    </Card>
                  ))}
                </Content>
              </CardBoard>
            ))}
          </CardScroll>
        </Container>
      </Page>
    </>
  );
};

export default KanbanProject;
